/* UI theme preference.
 *
 *   - user.theme === 'dark'  → dark
 *   - user.theme === 'light' → light
 *   - user.theme == null     → light, the product default
 *
 * Light is the default because it is the house look: the palette comes straight
 * from the marketing site. A user who has never opened Settings gets it, which
 * is every account created before theming shipped.
 *
 * The value is stored on the account so it follows the user between devices, and
 * mirrored into localStorage because the account value arrives too late to paint
 * with: /auth/me resolves after the first frame, and Login/Signup render with no
 * user at all. The inline script in index.html reads that mirror before first
 * paint, so there is no flash of the wrong theme.
 *
 * Only an *explicit* choice is ever mirrored. "Nothing stored" has to keep
 * meaning "no preference", so that the default can be changed in one place
 * without every returning browser holding a stale pin of the old one.
 *
 * - useTheme() returns the effective theme for JS-driven color (the canvas, the
 *   3D stage) — see lib/cssColor.ts.
 * - useApplyTheme() reflects it onto the document root as `data-theme`, which
 *   every CSS token in theme.css keys off. Call it once near the app shell. */
import { useEffect } from 'react';
import type { Theme } from '@venturewrist/shared';
import { useAuth } from './queries';

/** Mirrors the key read by the pre-paint script in index.html. */
export const THEME_STORAGE_KEY = 'vw.theme';

/** What a user with no stored preference gets. Also hard-coded as the
 *  `data-theme` attribute in index.html, so the two must agree. */
export const DEFAULT_THEME: Theme = 'light';

export function useTheme(): Theme {
  const { user } = useAuth();
  return user?.theme ?? DEFAULT_THEME;
}

export function useApplyTheme(): void {
  const { user } = useAuth();
  const theme = useTheme();
  const explicit = user?.theme ?? null;
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      // Mirror only an explicit choice; clear the key otherwise. Writing the
      // effective theme here would pin the current default into every browser
      // that ever loaded the app, so changing the default later would not reach
      // returning users.
      if (explicit) localStorage.setItem(THEME_STORAGE_KEY, explicit);
      else localStorage.removeItem(THEME_STORAGE_KEY);
    } catch { /* private mode / storage blocked */ }
  }, [theme, explicit]);
}
